import React, { useCallback, useState } from "react"
import { useTheme } from "~/ui/styled"
import Animated, {
  useSharedValue,
  withSequence,
  withTiming,
  useAnimatedStyle,
  runOnJS
} from "~/ui/reanimated"
import { ColumnFlex, CenterFlex, Text, Button, Image } from "~components/El"
import { Avatar } from "~widgets/avatar"
import { RedPacketType } from "~proto"
import { Message, useStores } from "~models"
import { globalToast } from "~utils";
import useGroupActions from "~hooks/useGroupActions"
import { useChatList } from "~hooks/useChatList"
import { PopupRoot, PopupImageBackground, PopupClose } from "../components/PopupShell"

const redPacketBg = require("~assets/images/chat/red-packet-bg.jpg")

interface Props {
  message: Message
  onOpen: () => void,
  onClose: () => void
}

const RedPacketPopup: React.FC<Props> = ({ message, onOpen, onClose }) => {
  const theme = useTheme()
  const { walletStore, messageStore } = useStores()
  const { openRedPacketDetailPopup, openExclusiveRedPacketDetailPopup } = useChatList()
  const { handleRedPacket } = useGroupActions()
  const [loading, setLoading] = useState(false)
  const { attach = {}, sender = {} } = message
  const redpacketInfo = attach?.redpacket_info

  const scale = useSharedValue(1)

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  const onRedPacket = useCallback(async () => {
    if (loading) return
    setLoading(true)
    handleRedPacket({
        redpacket_id: attach?.redpacket_info?.redpacket_id,
    }, (data) => {
      const redpacketData = data?.redpacket_data
      messageStore.syncRedPacketMessageByDetail(redpacketData)
      walletStore.getWalletInfo()
      setLoading(false)
      onOpen()
      if ([RedPacketType.EXCLUSIVE, RedPacketType.PRIVATE].includes(redpacketData?.redpacket_type)) {
        openExclusiveRedPacketDetailPopup({ message, redpacketData })
      } else {
        openRedPacketDetailPopup({ message, redpacketData })
      }
    }, (error) => {
      setLoading(false)
      globalToast.show(error?.message || "领取失败")
    })
  }, [loading, onOpen])

  const handlePress = useCallback(() => {
    scale.value = withSequence(
      withTiming(0.85, { duration: 80 }),
      withTiming(1.15, { duration: 120 }),
      withTiming(1, { duration: 80 }, () => {
        runOnJS(onRedPacket)()
      })
    )
  }, [onRedPacket])

  return (
    <PopupRoot width="100%">
      <PopupImageBackground src={redPacketBg} resizeMode="cover">
        <ColumnFlex width="100%" height="100%" alignItems="center">
          <CenterFlex
            width="60px"
            height="60px"
            padding="2px"
            mt="90px"
            borderRadius="30px"
            bg={theme.colors.blockBackground}
          >
            <Avatar avatar={sender?.avatar} size={56} name={sender?.name} />
          </CenterFlex>

          <Text mt="15px" fontSize={18} color="#ffe6bd" fontWeight="600">
            {sender?.name || ""}的红包
          </Text>
          <CenterFlex width="100%" padding="0 12px" mt="5px">
            <Text numberOfLines={2} fontSize={13} color="#ffe6bd">
              {redpacketInfo?.blessing_words || "恭喜发财，大吉大利"}
            </Text>
          </CenterFlex>

          <Animated.View style={[{ marginTop: 82 }, animatedStyle]}>
            <Button
              width="72px"
              height="72px"
              activeOpacity={1.0}
              onClick={handlePress}
              disabled={loading}
            >
              <Image
                width="100%"
                height="100%"
                src={require("~assets/images/chat/icon-open.png")}
              />
            </Button>
          </Animated.View>
        </ColumnFlex>
      </PopupImageBackground>
      <PopupClose onClose={onClose} />
    </PopupRoot>
  )
}

export default RedPacketPopup
